import { isTurnLive, isTurnRegistered, useActiveChatId, useMessagesStore } from '@renderer/chat/store'
import { useSubscribeToChat } from '@renderer/chat/hooks/use-subscribe-to-chat'
import { makeSynthContinuationId } from '@renderer/chat/utils/synth-continuation'
import { useCallback, useEffect, useRef } from 'react'

import type { StagedAction } from './types'

export interface ResumeChatOptions {
  /** Overrides the resume hook label with the user's answer summary. */
  label?: string
}

/** The resume a resolved action asks for, or null when it asks for none. */
function resolveChatResume(
  action: StagedAction | null,
  opts?: ResumeChatOptions,
): { conversationId: string; label: string } | null {
  if (!action?.conversationId) return null
  const resumeChatHook = action.hooks?.find(
    (h) => h.trigger === 'execution_finished' && h.effect?.type === 'resume_chat',
  )
  if (!action.metadata?.approvalAutomation && !resumeChatHook) return null
  return {
    conversationId: action.conversationId,
    label: opts?.label || resumeChatHook?.effect?.label || 'Continuing…',
  }
}

/** Adds the optimistic answer bubble and follows the backend's resumed turn. */
export function useResumeChatAfterInvoke(): (action: StagedAction | null, opts?: ResumeChatOptions) => void {
  const { subscribe } = useSubscribeToChat()
  const { appendMessage } = useMessagesStore()
  const activeChatId = useActiveChatId()
  const activeChatIdRef = useRef(activeChatId)

  useEffect(() => {
    activeChatIdRef.current = activeChatId
  }, [activeChatId])

  return useCallback(
    (action: StagedAction | null, opts?: ResumeChatOptions) => {
      const resume = resolveChatResume(action, opts)
      if (!resume) return
      const { conversationId, label } = resume

      if (activeChatIdRef.current !== conversationId) return
      if (isTurnRegistered(conversationId)) return

      if (!isTurnLive(conversationId)) {
        appendMessage(conversationId, {
          id: makeSynthContinuationId(conversationId),
          role: 'user',
          conversation_id: conversationId,
          content: label,
          mentions: [],
        })
      }

      // The answer endpoint returns only after the backend has enqueued and
      // started the follow-up, so subscribe immediately. The turn registry
      // dedupes this against the conversations-gateway recovery path.
      subscribe(conversationId)
    },
    [appendMessage, subscribe],
  )
}
